// src/pages/ZenWalkPage.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence, useScroll } from 'framer-motion';
import './ZenWalkPage.css';

const routeSpots = [
    {
        id: 'spot0',
        top: '10%', left: '40%',
        nameJa: '金沢之旅宿 あけぼの（出発）',
        nameEn: 'Kanazawa Stay Akebono (Departure)',
        descJa: '日常から切り離された静寂の宿。ここから、己と向き合う思索の旅が始まります。',
        descEn: 'A sanctuary of silence separated from the everyday. Your journey of contemplation begins here.',
        map: 'https://maps.app.goo.gl/wY3MskWzS5z6rG8v7',
        image: '/images/spot-akebono.jpg',
        icon: <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8h5z" />,
        segmentInfo: { top: '16.5%', left: '40%', timeJa: '徒歩8分', timeEn: '8 min walk', dist: '600m' }
    },
    {
        id: 'spot1',
        top: '23%', left: '40%',
        nameJa: '大乗寺坂',
        nameEn: 'Daijo-ji Slope',
        descJa: '長く続く石段。一歩一歩登る身体的な制約を感じながら、金沢の歴史を味わう道。',
        descEn: 'A long stone slope. Feel the physical constraints with every step as you digest the layered history of Kanazawa.',
        map: 'https://maps.app.goo.gl/T6pYq1j6Y2U6v6h9',
        image: '/images/spot-slope.jpg',
        icon: <path d="M4 22h4v-4h4v-4h4v-4h4V6h-4v4h-4v4h-4v4H4v4z" />,
        segmentInfo: { top: '29.5%', left: '40%', timeJa: '徒歩6分', timeEn: '6 min walk', dist: '450m' }
    },
    {
        id: 'spot2',
        top: '36%', left: '40%',
        nameJa: '松風閣庭園',
        nameEn: 'Shofukukaku Garden',
        descJa: '加賀八家・本多家の名残。重層的な石垣と古木が落とす深い影に「幽玄」を見出します。',
        descEn: 'Relics of the Honda family. Find "Yugen" (subtle grace) in the deep shadows cast by ancient trees and stone walls.',
        map: 'https://maps.app.goo.gl/kX3Yq1j6Y2U6v6h9',
        image: '/images/spot-garden.jpg',
        icon: <path d="M12 21v-6c-3.5 0-6-2.5-6-6 0-3 3-5 6-5s6 2 6 5c0 3.5-2.5 6-6 6v6h-2z" />,
        segmentInfo: { top: '43%', left: '40%', timeJa: '徒歩2分', timeEn: '2 min walk', dist: '150m' }
    },
    {
        id: 'spot3',
        top: '50%', left: '40%',
        nameJa: '中村記念美術館',
        nameEn: 'Nakamura Memorial Museum',
        descJa: '茶道の文化と禅の関わり。「用の美」に触れ、静寂の中での一服が心を整えます。',
        descEn: 'Explore the link between Tea Ceremony and Zen. Let the "beauty of utility" and a bowl of tea settle your soul.',
        map: 'https://maps.app.goo.gl/mX3Yq1j6Y2U6v6h9',
        image: '/images/spot-museum.jpg',
        icon: <path d="M10 50l40-30 40 30v30H10V50z" />,
        segmentInfo: { top: '56.5%', left: '40%', timeJa: '徒歩5分', timeEn: '5 min walk', dist: '400m' }
    },
    {
        id: 'spot4',
        top: '63%', left: '40%',
        nameJa: '鈴木大拙 生誕地碑',
        nameEn: 'D.T. Suzuki Birthplace',
        descJa: '大拙が生まれ育った静寂の地。少年時代の彼も、この同じ空気を吸い、思索を深めていました。',
        descEn: 'The quiet soil where Daisetz was born. Imagine the young philosopher breathing this same air.',
        map: 'https://maps.app.goo.gl/nX3Yq1j6Y2U6v6h9',
        image: '/images/spot-monument.jpg',
        icon: <path d="M8 22h8v-2H8v2zm2-4h4V4h-4v14z" />,
        segmentInfo: { top: '69.5%', left: '40%', timeJa: '徒歩3分', timeEn: '3 min walk', dist: '250m' }
    },
    {
        id: 'spot5',
        top: '76%', left: '40%',
        nameJa: '鈴木大拙館',
        nameEn: 'Suzuki Daisetz Museum',
        descJa: '水鏡の庭。風が水面を揺らすように、心のさざなみが自然と消えていく、思索の旅の最深部。',
        descEn: 'The Water Mirror Garden. The core of your journey, where the ripples of the mind naturally fade.',
        map: 'https://maps.app.goo.gl/oX3Yq1j6Y2U6v6h9',
        image: '/images/spot-daisetz.jpg',
        icon: <path d="M4 14h16v6H4v-6z M8 6h8v6H8V6z M2 22h20v2H2v-2z" />,
        segmentInfo: { top: '83%', left: '40%', timeJa: '徒歩10分', timeEn: '10 min walk', dist: '800m' }
    },
    {
        id: 'spot6',
        top: '90%', left: '40%',
        nameJa: '金沢之旅宿 あけぼの（帰着）',
        nameEn: 'Kanazawa Stay Akebono (Return)',
        descJa: '思索の旅の帰着点。テレビのない空間で、得られた静寂を自分の中に沈殿させます。',
        descEn: 'The destination. In a space without television, allow the silence you discovered to settle deep within you.',
        map: 'https://maps.app.goo.gl/pX3Yq1j6Y2U6v6h9',
        image: '/images/room6.jpg',
        icon: <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8h5z" />
    }
];

const DEFAULT_BG_IMAGE = '/images/zen-walk1.jpg';
const SITE_DOMAIN = 'https://kanazawa-stay-akebono.com';

const ZenWalkPage = () => {
    const { pathname } = useLocation();
    const isEnglish = pathname.startsWith('/en');
    const [activeSpot, setActiveSpot] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
    const audioRef = useRef(null);

    const toggleAudio = () => {
        if (!audioRef.current) return;
        if (isPlaying) { audioRef.current.pause(); } else { audioRef.current.play(); }
        setIsPlaying(!isPlaying);
    };

    const mapContainerRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: mapContainerRef,
        offset: ["start 90%", "60% center"]
    });

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 900);
        window.addEventListener('resize', handleResize);
        window.scrollTo(0, 0);
        return () => {
            window.removeEventListener('resize', handleResize);
            if (audioRef.current) audioRef.current.pause();
        };
    }, [pathname]);

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": isEnglish ? "Zen Walking Route in Kanazawa" : "金沢・本多町 思索の道程（歩行禅ルート）",
        "description": isEnglish ? "A mindful Zen walking route exploring the birthplace of D.T. Suzuki, starting and ending at Kanazawa Stay Akebono." : "金沢之旅宿あけぼのを起点とした、静寂と哲学を巡る散策ルート。", "image": `${SITE_DOMAIN}${DEFAULT_BG_IMAGE}`,
        "step": routeSpots.map((spot, index) => ({
            "@type": "HowToStep",
            "name": isEnglish ? spot.nameEn : spot.nameJa,
            "text": isEnglish ? spot.descEn : spot.descJa,
            "position": index + 1,
            "url": `${SITE_DOMAIN}${isEnglish ? '/en' : ''}/zen-walk`
        }))
    };

    return (
        <>
            <Helmet>
                <html lang={isEnglish ? 'en' : 'ja'} />
                <title>{isEnglish ? 'Zen Walk in Kanazawa: The Roots of D.T. Suzuki | Kanazawa Stay Akebono' : '禅のまち本多町と思索の道程 | 金沢之旅宿 あけぼの'}</title>
                <meta name="description" content={isEnglish ? "Experience a mindful Zen walking route in Kanazawa. Follow the footsteps of world-renowned philosopher D.T. Suzuki through his peaceful hometown, starting from Kanazawa Stay Akebono." : "金沢之旅宿あけぼのを出発し、本多町の静寂を巡る「歩行禅」のルート。"} />
                <meta property="og:url" content={`${SITE_DOMAIN}${pathname}`} />
                <meta property="og:image" content={`${SITE_DOMAIN}${DEFAULT_BG_IMAGE}`} />
                <link rel="canonical" href={`${SITE_DOMAIN}${pathname}`} />
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            </Helmet>

            <div className="zen-walk-page winding-route-theme">
                <audio ref={audioRef} src="/audio/zen-ambient.mp3" loop preload="none" />
                <button className={`zen-audio-btn ${isPlaying ? 'playing' : ''}`} onClick={toggleAudio}>
                    <div className="audio-wave"><span></span><span></span><span></span></div>
                    {isEnglish ? (isPlaying ? 'Listen to Silence' : 'Play Sound') : (isPlaying ? '静寂を聴く' : '音を奏でる')}
                </button>

                <section className="zen-hero" style={{ backgroundImage: `url(${DEFAULT_BG_IMAGE})` }}>
                    <div className="zen-hero-overlay"></div>
                    <div className="zen-hero-content">
                        <h1 className="cinzel-font">{isEnglish ? 'Honda-machi: A Zen Walk in D.T. Suzuki\'s Hometown' : '禅のまち 本多町'}</h1>
                        <p>{isEnglish ? 'Immerse yourself in the birthplace of the world-renowned philosopher D.T. Suzuki.' : 'そこは世界的哲学者・鈴木大拙を生んだ、禅のまち'}</p>
                    </div>
                </section>

                <div className="akebono-sp-container">
                    <section className="zen-philosophy-section">
                        <h2 className="zen-section-title cinzel-font">Philosophy</h2>
                        <div className="suzuki-profile-card">
                            <div className="suzuki-image-wrapper">
                                {/* 画像パスは適宜調整してください */}
                                <img src="/images/dt-suzuki.jpg" alt={isEnglish ? "Daisetz Teitaro Suzuki - World-renowned Zen philosopher from Kanazawa" : "世界的哲学者 鈴木大拙（金沢市本多町出身）"} />
                            </div>
                            <div className="suzuki-info">
                                <h3 className="suzuki-name-title">
                                    {isEnglish ? "Daisetz Teitaro Suzuki" : "鈴木 大拙"}
                                    <span className="suzuki-years">1870 - 1966</span>
                                </h3>
                                <p className="suzuki-bio-text">
                                    {isEnglish
                                        ? "Born in Honda-machi, Kanazawa. A world-renowned Buddhist scholar and philosopher who introduced 'Zen' and Japanese culture to the West. His profound teachings continue to inspire global thinkers, artists, and creators today."
                                        : "金沢市本多町生まれ。日本の禅文化を広く世界に紹介し、現代の「ZEN」の礎を築いた世界的哲学者。その思想はスティーブ・ジョブズをはじめ、世界中の知識人や芸術家に多大な影響を与え続けています。"
                                    }
                                </p>
                            </div>
                        </div>

                        <div className="zen-detox-quote-box">
                            <h3 className="zen-quote">{isEnglish ? '"The elbow does not bend outward."' : '「ひじ、外に曲がらず」'}</h3>
                            <p className="zen-quote-author">— D.T. Suzuki</p>
                            <div className="quote-divider"></div>
                            <p className="zen-quote-text">
                                {isEnglish
                                    ? "By accepting the laws of nature (constraints) rather than denying them, one attains true freedom. The winding slopes of Hondamachi and the minimally informed space of Akebono are beautiful constraints designed for you to experience D.T. Suzuki's Zen philosophy through your own body."
                                    : <>与えられた不自由（理）を否定せず、そのまま受け入れることで真の自由を得る。本多町の曲がりくねった坂道、情報の少ない「あけぼの」の空間。それは、世界的哲学者・<span className="no-break">鈴木大拙</span>が説いた禅の哲学を身体で感じるための、美しき制約です。</>
                                }
                            </p>
                        </div>
                    </section>

                    <section className="zen-gallery-section">
                        <div className="zen-gallery-grid">
                            <motion.div className="zen-gallery-img" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}><img src="/images/hero2.jpg" alt="Kanazawa Stay Akebono Room" /></motion.div>
                            <motion.div className="zen-gallery-img" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}><img src="/images/machiya2.jpg" alt="Kanazawa Stay Akebono Details" /></motion.div>
                        </div>
                    </section>

                    <section className="zen-route-map-section">
                        <h2 className="zen-section-title cinzel-font">Interactive Route</h2>
                        <div className="winding-path-container" ref={mapContainerRef}>

                            <AnimatePresence mode="popLayout">
                                <motion.div
                                    key={activeSpot ? activeSpot.id : 'default-bg'}
                                    className="route-dynamic-bg"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: isMobile ? 0.1 : 0.15 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 1.0 }}
                                    style={{ backgroundImage: `url(${activeSpot ? activeSpot.image : DEFAULT_BG_IMAGE})` }}
                                />
                            </AnimatePresence>

                            <svg className="winding-svg-line" viewBox="0 0 100 100" preserveAspectRatio="none">
                                <motion.path d="M 40 10 L 40 90" stroke="#C5A059" strokeWidth="0.5" fill="none" strokeDasharray="1 1" style={{ pathLength: scrollYProgress }} />
                            </svg>

                            {routeSpots.map((spot, index) => spot.segmentInfo && (
                                <motion.div key={`seg-${index}`} className="route-segment-info" style={{ top: spot.segmentInfo.top, left: spot.segmentInfo.left }} initial={{ opacity: 0, x: "-50%", y: "calc(-50% + 15px)" }} whileInView={{ opacity: 1, x: "-50%", y: "-50%" }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: 0.1 }}>
                                    <div className="segment-text-box">
                                        <span className="segment-time">{isEnglish ? spot.segmentInfo.timeEn : spot.segmentInfo.timeJa}</span>
                                        <span className="segment-dist">{spot.segmentInfo.dist}</span>
                                    </div>
                                </motion.div>
                            ))}

                            {routeSpots.map((spot, index) => (
                                <motion.div key={spot.id} className={`route-node ${activeSpot?.id === spot.id ? 'active' : ''}`} style={{ top: spot.top, left: spot.left }} onClick={() => setActiveSpot(spot)} initial={{ opacity: 0, scale: 0.5, x: "-50%", y: "-50%" }} whileInView={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.4 }}>
                                    <div className="node-pulse"></div>
                                    <div className="node-icon-wrapper"><svg viewBox="0 0 24 24" className="spot-icon-svg" fill="currentColor">{spot.icon}</svg></div>
                                    <span className={`node-label ${index % 2 === 0 ? 'label-right' : 'label-left'} ${isEnglish ? 'label-en' : 'label-ja'}`}>
                                        {isEnglish ? spot.nameEn : spot.nameJa}
                                    </span>
                                </motion.div>
                            ))}

                            <AnimatePresence>
                                {activeSpot && (
                                    <motion.div
                                        className="map-side-drawer"
                                        initial={isMobile ? { y: '100%', x: 0, opacity: 0 } : { x: '100%', y: 0, opacity: 0 }}
                                        animate={{ x: 0, y: 0, opacity: 1 }}
                                        exit={isMobile ? { y: '100%', x: 0, opacity: 0 } : { x: '100%', y: 0, opacity: 0 }}
                                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                    >
                                        <button className="drawer-close-btn" onClick={(e) => { e.stopPropagation(); setActiveSpot(null); }}>×</button>
                                        {!isMobile && (
                                            <div className="drawer-image-box"><img src={activeSpot.image} alt={isEnglish ? activeSpot.nameEn : activeSpot.nameJa} /></div>
                                        )}
                                        <div className="drawer-content-box">
                                            <span className="popup-step-num cinzel-font">SPOT 0{routeSpots.findIndex(s => s.id === activeSpot.id) + 1}</span>
                                            <h4 className="popup-title">{isEnglish ? activeSpot.nameEn : activeSpot.nameJa}</h4>
                                            <div className="quote-divider" style={{ margin: '15px 0', width: '40px' }}></div>
                                            <p className="popup-desc">{isEnglish ? activeSpot.descEn : activeSpot.descJa}</p>
                                            <div className="stylish-link-container">
                                                <a href={activeSpot.map} target="_blank" rel="noopener noreferrer" className="popup-map-link stylish-underline">
                                                    Google Map ↗
                                                </a>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </section>

                    <section className="zen-afterglow-section">
                        <h2 className="zen-section-title cinzel-font">{isEnglish ? 'The Ritual of Return' : '禅の続き、あけぼの 時間。'}</h2>
                        <div className="afterglow-grid">
                            <motion.div className="afterglow-item" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
                                <div className="afterglow-img-box"><img src="/images/garden2.jpg" alt="庭を見て耽る" /></div>
                                <div className="afterglow-text-box">
                                    <h4 className="cinzel-font">{isEnglish ? 'Contemplation at the Garden' : '庭を見て 耽る'}</h4>
                                    <p>
                                        {isEnglish
                                            ? "Watch the light shift across the moss. In this silent dialogue with the garden, time stretches, and the boundaries between self and world gently dissolve."
                                            : "移ろう光、揺れる緑。庭と対話し、ただ耽る。散策で研ぎ澄まされ、深い充足へ。"
                                        }
                                    </p>
                                </div>
                            </motion.div>

                            <motion.div className="afterglow-item inverted" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}>
                                <div className="afterglow-img-box"><img src="/images/machiya1.jpg" alt="和室での静寂" /></div>
                                <div className="afterglow-text-box">
                                    <h4 className="cinzel-font">{isEnglish ? 'Stillness in the Tatami Room' : '静かな夜、和室で'}</h4>
                                    <p>
                                        {isEnglish
                                            ? "The scent of tatami, the depth of the night. In a room without digital noise, you listen to the sound of your own breath and the settling silence of Kanazawa."
                                            : "畳の香り、更ける夜。己の呼吸と向き合う。本多町の静寂を馴染ませる、余白の時間。"
                                        }
                                    </p>
                                </div>
                            </motion.div>

                            <motion.div className="afterglow-item" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.4 }}>
                                <div className="afterglow-img-box"><img src="/images/tea-2.jpg" alt="茶と書を楽しむ" /></div>
                                <div className="afterglow-text-box">
                                    <h4 className="cinzel-font">{isEnglish ? 'The Art of Tea and Calligraphy' : '茶と書、静かな自己表現'}</h4>
                                    <p>
                                        {isEnglish
                                            ? "Whisk your own matcha or glide a brush across paper in the quiet of your room. For those seeking a deeper connection to Kanazawa's traditions, we also offer exclusive private cultural experiences guided by local masters."
                                            : "より深い文化体験へ"
                                        }
                                    </p>
                                    <div style={{ marginTop: '25px' }}>
                                        <a href={isEnglish ? "/en/special-offer" : "/special-offer"} className="popup-map-link stylish-underline">
                                            {isEnglish ? 'Explore Cultural Experiences ↗' : '特別文化体験の詳細を見る ↗'}
                                        </a>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </section>

                    <section className="zen-closing-quote">
                        <div className="quote-divider"></div>
                        <p className="closing-text">
                            {isEnglish
                                ? "Experience the winding silence of Hondamachi."
                                : "本多町、静寂のその先へ。"
                            }
                        </p>
                    </section>
                </div>
            </div>
        </>
    );
};

export default ZenWalkPage;